import { useState, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import type { ComponentPropsWithoutRef, Dispatch, ReactNode, SetStateAction } from 'react'

interface CreateTeleporterOptions {
  multiSources?: boolean
}

type TargetElement = HTMLDivElement | null
type ElementSetter = (element: TargetElement) => void
type SetRef = {
  current: Dispatch<SetStateAction<TargetElement>> | null
}

interface TeleporterContext {
  element: TargetElement
  set: SetRef | null
}

interface TargetProps extends ComponentPropsWithoutRef<'div'> {
  as?: 'div'
}

interface SourceProps {
  children?: ReactNode | ((element: HTMLDivElement) => ReactNode)
}

export const createTeleporter = ({ multiSources }: CreateTeleporterOptions = {}) => {
  const context: TeleporterContext = { element: null, set: null }

  const setElement: ElementSetter = (element) => {
    context.element = element
    if (context.set && context.set.current) {
      context.set.current(element)
    }
  }

  const useTargetRef = () => setElement

  const Target = ({ as: As = 'div', ...props }: TargetProps) => {
    return <As ref={setElement} {...props} />
  }

  const Source = ({ children }: SourceProps) => {
    const [element, setElement] = useState<TargetElement>(null)

    useLayoutEffect(() => {
      const setRef: SetRef = { current: setElement }
      let previousSet: SetRef | undefined

      if (context.set) {
        previousSet = context.set
        if (!multiSources && context.set.current) {
          context.set.current(null)
        }
      }

      context.set = setRef
      if (context.element !== undefined) {
        setElement(context.element)
      }

      return () => {
        setRef.current = null
        context.set = null

        if (previousSet && previousSet.current) {
          context.set = previousSet
          if (context.element !== undefined) {
            previousSet.current(context.element)
          }
        }
      }
    }, [])
    if (!element) return null
    const content = typeof children === 'function' ? children(element) : children
    return createPortal(content, element)
  }

  return { Source, Target, useTargetRef }
}
